import { union } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { showToast } from '~/components/Shared/EcToast/showToast';
import { Box } from '~/eds/src/components/Box/Box';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { useTableSelection } from '~/eds/src/hooks/useTableSelection';
import { TableViewContextType } from '~/enums/TableViewType';
import { Filter } from '~/evifields/src/types/Filter';
import { CrossFilters } from '~/features/advanced-search/cross-filters/CrossFilters';
import { Filters } from '~/features/advanced-search/filters/Filters';
import { QueryBuilder } from '~/features/advanced-search/query-builder/QueryBuilder';
import { QueryFilters } from '~/features/advanced-search/query-filters/QueryFilters';
import { isActiveQuery } from '~/features/advanced-search/query-filters/QueryFilters.utils';
import { ReadOnlyComplexQuery } from '~/features/advanced-search/read-only-complex-query/ReadOnlyComplexQuery';
import { SearchQuery } from '~/features/advanced-search/types';
import {
  buildComplexQueryWithCrossFilters,
  buildQuery,
} from '~/features/advanced-search/utils/build-query';
import { getEmptyFilters } from '~/features/advanced-search/utils/filters';
import { getDefaultChatContext } from '~/features/ask-anything/ask-anything-button/utils';
import { useFieldsWithLazyLoad } from '~/features/filters/useFieldsWithLazyLoad';
import { AddSearchResultsToGroup } from '~/features/search/AddSearchResultsToGroup/AddSearchResultsToGroup';
import { useDocumentsBulkActionsPanel } from '~/features/search/useDocumentsBulkActionsPanel';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { usePinnedFilters as usePinnedFiltersDeprecated } from '~/hooks/usePinnedFilters_DEPRECATED';
import { useTableSettings } from '~/hooks/useTableSettings';
import { api } from '~/redux/api/createApi';
import { Dashboard } from '~/redux/api/methods/dashboardV2';
import { slices } from '~/redux/slices';
import {
  MODAL_DOCUMENTS_COLUMN_FIELDS_SAVE,
  MODAL_DOCUMENTS_COLUMN_FIELDS_VIEW_SWITCH,
} from '~/types/modal.types';
import { PilotId } from '~/types/PilotId';
import { ERROR } from '~/types/toast.types';
import { capitalize } from '~/utils/strings';
import { DEFAULT_TABLE_SETTINGS } from '~/utils/table';
import { testIsAdmin } from '~/utils/user';

import { getSearchableFields } from '../SearchV2/utils';
import { trackSegment } from '../SegmentAnalytics';
import BaseLoadView from '../Shared/BaseLoadView/BaseLoadView';
import BaseSaveView from '../Shared/BaseSaveView/BaseSaveView';
import SearchV3BooleanSearchBar from '../Shared/SearchV3BooleanSearchBar/SearchV3BooleanSearchBar';
import { DashboardV2Charts } from './DashboardV2Charts/DashboardV2Charts';
import DashboardV2SearchResults from './DashboardV2SearchResults/DashboardV2SearchResults';
import { EditCharts } from './EditCharts/EditCharts';
import SaveDashboard from './SaveDashboard/SaveDashboard';

type Props = {
  dashboard: Dashboard;
};

const DashboardV2 = ({ dashboard }: Props) => {
  const intl = useIntl();
  const user = useCurrentUser();

  const { data: searchFilters, isFetching: isFetchingFilters } =
    api.endpoints.getFilters.useQuery(undefined);

  const searchFiltersArray = useMemo(
    () => Object.values(searchFilters ?? {}),
    [searchFilters],
  );

  const searchFiltersList = useFieldsWithLazyLoad({
    filters: searchFiltersArray,
  });

  const [fetchTableView, tableViewResults] =
    api.endpoints.getCurrentTableView.useLazyQuery();
  const { data: currentTableView, isFetching: isFetchingCurrentTableView } =
    tableViewResults;

  const [isAddToGroupVisible, setIsAddToGroupVisible] = useState(false);
  const [isSaveViewVisible, setIsSaveViewVisible] = useState(false);
  const [isLoadViewVisible, setIsLoadViewVisible] = useState(false);

  // Pinned Filters
  const [localPinnedFilterIds, setLocalPinnedFilterIds] = useState<
    Array<PilotId | string>
  >([]);
  const {
    getPinnedFilters,
    result: {
      data: userPreferencesDefaultFilters,
      isFetching: isFetchingPinnedFilters,
    },
  } = usePinnedFiltersDeprecated(searchFilters);

  const [updatePinnedFilters] = api.endpoints.updatePinnedFilters.useMutation();

  const pinnedFiltersIds = useMemo(() => {
    return userPreferencesDefaultFilters?.map((filter) =>
      isNaN(Number(filter.fieldId)) ? filter.fieldId : Number(filter.fieldId),
    );
  }, [userPreferencesDefaultFilters]);

  const activeFilters = useSelector(
    slices.dashboardV2.selectors.selectActiveFilters,
  );
  const crossFilters = useSelector(
    slices.dashboardV2.selectors.selectCrossFilters,
  );

  const fieldValues = useSelector(
    slices.dashboardV2.selectors.selectFieldValues,
  );
  const fieldsWithLazyLoad = useFieldsWithLazyLoad({
    filters: Object.values(searchFilters || {}),
    fieldValues,
  });
  const activeDashboard = useSelector(
    slices.dashboardV2.selectors.selectActiveDashboard,
  );
  const page = useSelector(slices.dashboardV2.selectors.selectPage);
  const pageSize = useSelector(slices.dashboardV2.selectors.selectPageSize);
  const sortBy = useSelector(slices.dashboardV2.selectors.selectSortBy);
  const searchText = useSelector(slices.dashboardV2.selectors.selectSearchText);

  const queryBuilder = useSelector(
    slices.dashboardV2.selectors.selectQueryBuilder,
  );
  const queryFilters = useSelector(
    slices.dashboardV2.selectors.selectFilterQuery,
  );
  const queryBuilderPanelVisible = useSelector(
    slices.dashboardV2.selectors.selectQueryBuilderPanelVisible,
  );
  const columns = useSelector(slices.dashboardV2.selectors.selectColumnOrder);

  const selectedFilters = useSelector(
    slices.dashboardV2.selectors.selectSelectedFilters,
  );
  const chartFilters = useSelector(slices.dashboardV2.selectors.selectCharts);
  const intervals = useSelector(slices.dashboardV2.selectors.selectIntervals);
  const chartWidths = useSelector(
    slices.dashboardV2.selectors.selectChartWidths,
  );
  const hasDataChanges = useSelector(
    slices.dashboardV2.selectors.selectDashboardDirty,
  );

  const resultsCache = useSelector(
    slices.dashboardV2.selectors.selectResultsCache,
  );
  const tableSelection = useTableSelection();
  const canSaveDashboard = testIsAdmin(user) || dashboard.creatorId === user.id;

  /** tracks if redux active dashboard is the same as the route dashboard. */
  const isCurrentDashboardActive = activeDashboard.id === dashboard.id;

  const isBulkActionOnDashboardEnabled = useFlag(
    FlagType.DashboardV2DocumentsBulkActions,
  );
  const isQueryFiltersEnabled = useFlag(
    FlagType.QueryFiltersIntoSearchAndDashboard,
  );

  const query = useMemo(() => {
    const shouldUseQueryFilters = queryFilters && isQueryFiltersEnabled;
    if ((shouldUseQueryFilters || queryBuilder) && searchFilters) {
      const queryToUse = shouldUseQueryFilters
        ? isActiveQuery(queryFilters)
        : queryBuilder;
      return buildComplexQueryWithCrossFilters(
        queryToUse,
        crossFilters,
        searchFilters,
        searchText,
      );
    } else if (activeFilters?.length || crossFilters?.length || searchFilters) {
      return buildQuery({
        booleanQuery: searchText,
        crossFilters,
        filters: activeFilters,
        searchFilters,
      });
    } else {
      return [];
    }
  }, [
    activeFilters,
    crossFilters,
    queryBuilder,
    searchFilters,
    searchText,
    queryFilters,
  ]);

  const queryToSave = useMemo(() => {
    const shouldUseQueryFilters = queryFilters && isQueryFiltersEnabled;
    if ((shouldUseQueryFilters || queryBuilder) && searchFilters) {
      const queryToUse = shouldUseQueryFilters
        ? isActiveQuery(queryFilters)
        : queryBuilder;
      return buildComplexQueryWithCrossFilters(
        queryToUse,
        [],
        searchFilters,
        searchText,
      );
    } else if (activeFilters?.length || searchFilters) {
      return buildQuery({
        booleanQuery: searchText,
        filters: activeFilters,
        searchFilters,
      });
    } else {
      return [];
    }
  }, [
    activeFilters,
    crossFilters,
    queryBuilder,
    searchFilters,
    searchText,
    queryFilters,
  ]);

  const dashboardBooleanSearchText = dashboard.query.find(
    (q: any) => q.type === 'bool_text_search',
  )?.value;

  const dashboardData: Dashboard = useMemo(
    () => ({
      charts: chartFilters.map((item, index) => ({
        filter_id: item.id,
        sorted_index: index,
        interval: intervals[item.id],
        width: chartWidths[item.id] ? chartWidths[item.id] : undefined,
        type: item.type,
      })),
      columns: columns || [],
      filters: isQueryFiltersEnabled
        ? getEmptyFilters(queryFilters ?? []).map((f) => f.id.toString())
        : selectedFilters
            .filter((f) => !f.values.length && !f.operatorId)
            .map((f) => f.fieldId.toString()),
      id: dashboard.id,
      name: dashboard.name,
      entity: dashboard.entity,
      query,
      is_default: !!dashboard.is_default,
      creatorId: dashboard.creatorId,
    }),
    [
      dashboard,
      chartFilters,
      columns,
      selectedFilters,
      intervals,
      chartWidths,
      queryFilters,
    ],
  );

  const pinnedFilterIds = userPreferencesDefaultFilters?.map(
    (filter) => filter.fieldId,
  );

  const dispatch = useDispatch();

  const onSearch = (search: string) => {
    dispatch(slices.dashboardV2.actions.setSearchText(search));
    dispatch(slices.dashboardV2.actions.setPage(1));
    dispatch(
      slices.dashboardV2.actions.setDashboardDirty(
        dashboardBooleanSearchText !== search,
      ),
    );
    if (search) {
      trackSegment('searchKeywords', {
        name: search,
      });
    }
  };

  const hideQueryBuilderPanel = () => {
    dispatch(slices.dashboardV2.actions.setQueryBuilderPanelVisible(false));
  };
  const {
    data: searchV3Data,
    isLoading: isLoadingSearchResults,
    isFetching: isFetchingSearchResults,
    isError: isErrorSearchDocuments,
    refetch: refetchSearch,
  } = api.endpoints.searchV3Documents.useQuery(
    {
      query,
      page,
      pageSize,
      sortBy,
      filters: columns?.filter((column) => column !== 'name'),
      store_as_recent_search: false,
    },
    {
      skip: !isCurrentDashboardActive,
    },
  );

  const initDashboard = async () => {
    if (!searchFilters) return;

    const isDefaultDocumentsDashboard =
      dashboard.is_default && dashboard.name === 'Documents';

    if (isDefaultDocumentsDashboard) {
      const finalPinnedFilters =
        pinnedFilterIds ??
        (await getPinnedFilters('SEARCH').unwrap())?.data ??
        [];
      const dashboardWithPinnedFilters = {
        ...dashboard,
        filters: union(dashboard.filters, finalPinnedFilters),
      };

      dispatch(
        slices.dashboardV2.actions.initDashboard({
          dashboard: dashboardWithPinnedFilters,
          searchFilters,
          tableSettings,
        }),
      );
    } else {
      dispatch(
        slices.dashboardV2.actions.initDashboard({
          dashboard,
          searchFilters,
          tableSettings,
        }),
      );
    }
  };

  useEffect(() => {
    if (!!isErrorSearchDocuments) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'DashboardV2.searchDocumentsError',
          defaultMessage: 'An error occurred while searching for documents.',
          description: 'Error message when document search fails',
        }),
      );
    }
  }, [isErrorSearchDocuments]);

  const {
    tableSettings = {
      ...DEFAULT_TABLE_SETTINGS,
      columnOrder: dashboard.columns || [],
    },
  } = useTableSettings(`DASHBOARD:${dashboard.id}`);

  useEffect(() => {
    initDashboard();
  }, [searchFilters, dashboard]);

  const isLoading =
    isFetchingFilters ||
    isLoadingSearchResults ||
    isFetchingPinnedFilters ||
    isFetchingCurrentTableView;

  useEffect(() => {
    getPinnedFilters('SEARCH');
  }, []);

  useEffect(() => {
    if (pinnedFiltersIds && pinnedFiltersIds.length) {
      setLocalPinnedFilterIds(pinnedFiltersIds ?? []);
    }
  }, [pinnedFiltersIds]);

  const openQueryBuilderPanel = () => {
    dispatch(slices.dashboardV2.actions.setQueryBuilderPanelVisible(true));
  };

  const onFilterChange = (filters: Filter[]) => {
    dispatch(slices.dashboardV2.actions.setSelectedFilters(filters));
    dispatch(slices.dashboardV2.actions.setPage(1));
    dispatch(slices.dashboardV2.actions.setDashboardDirty(true));
  };

  const onCrossFiltersChange = (filters: Filter[]) => {
    dispatch(slices.dashboardV2.actions.setCrossFilters(filters));
  };

  const onFiltersReset = () => {
    if (searchFilters) {
      dispatch(slices.dashboardV2.actions.setSelectedFilters([]));
      dispatch(slices.dashboardV2.actions.setCrossFilters([]));
      dispatch(slices.dashboardV2.actions.setPage(1));
      initDashboard();
    }
  };

  const resetSearchResultSelection = () => {
    tableSelection.clear();
  };

  const handleUpdatePinnedFilters = async (
    updatedPinnedFilters: Array<PilotId | string>,
  ) => {
    setLocalPinnedFilterIds(updatedPinnedFilters);
    await updatePinnedFilters({
      context: 'SEARCH',
      pinnedFilters: updatedPinnedFilters as string[],
    });
    getPinnedFilters('SEARCH');
  };

  // Viewset
  const showSaveView = useCallback(() => {
    setIsSaveViewVisible(true);
  }, []);

  const showLoadView = useCallback(() => {
    setIsLoadViewVisible(true);
  }, []);

  const refreshSearchResult = useCallback(() => {
    resetSearchResultSelection();
    refetchSearch();
  }, []);

  useEffect(() => {
    if (searchV3Data?.results) {
      if (searchV3Data.results.length > 0) {
        dispatch(
          slices.chatbot.actions.setContext([
            getDefaultChatContext(intl),
            {
              label: intl.formatMessage({
                id: 'DashboardV2.allResultsLabel',
                defaultMessage: 'All Results',
                description: 'Label for all search results context',
              }),
              query: query,
              title: intl.formatMessage(
                {
                  id: 'DashboardV2.allResultsTitle',
                  defaultMessage: 'All Results ({total})',
                  description:
                    'Title showing all results with total count. Example: total = 150',
                },
                {
                  total: searchV3Data.meta.total,
                },
              ),
              icon: 'search',
              primary: true,
              searchEntity: 'document',
              selectedIds: [],
              id: dashboard.id,
              type: 'all_results',
            },
          ]),
        );
      }

      dispatch(
        slices.dashboardV2.actions.patchSearchResultsCache(
          searchV3Data.results,
        ),
      );
    }

    return () => {
      dispatch(
        slices.chatbot.actions.setContext([getDefaultChatContext(intl)]),
      );
    };
  }, [searchV3Data]);

  const { panel, limitExceededModal, setActivePanel } =
    useDocumentsBulkActionsPanel({
      selectedDocumentIds: tableSelection?.selectedIds,
      isAllSelected: tableSelection?.isAllSelected,
      totalCount: searchV3Data?.meta?.total ?? 0,
      query: query,
      searchAPI: 'v3',
      onActionCompleted: refreshSearchResult,
      resultsCache: resultsCache,
    });

  const handleMove = useCallback(() => {
    setActivePanel('move');
  }, []);

  const handleCopy = useCallback(() => {
    setActivePanel('copy');
  }, []);

  const handleEdit = useCallback(() => {
    setActivePanel('edit');
  }, []);

  const handleEditClauses = useCallback(() => {
    setActivePanel('edit-clauses');
  }, []);

  const handleDelete = useCallback(() => {
    setActivePanel('delete');
  }, []);

  const handleSuccessAddToGroup = useCallback(() => {
    refreshSearchResult();
    setIsAddToGroupVisible(false);
  }, []);

  const handleSearchQueryBuilder = (queryBuilderPayload: SearchQuery) => {
    dispatch(slices.dashboardV2.actions.setQueryBuilder(queryBuilderPayload));
    dispatch(slices.dashboardV2.actions.setPage(1));
    dispatch(slices.dashboardV2.actions.setDashboardDirty(true));
    dispatch(slices.dashboardV2.actions.setQueryBuilderPanelVisible(false));
    dispatch(slices.dashboardV2.actions.setSearchText(''));
  };

  const onSaveSuccess = () => {
    dispatch(slices.dashboardV2.actions.setDashboardDirty(false));
  };

  const loadingContent = {
    isLoading: !isCurrentDashboardActive,
    message: intl.formatMessage({
      id: 'DashboardV2.loadingDashboard',
      defaultMessage: 'Loading Dashboard...',
      description: 'Loading message for dashboard',
    }),
  };
  const loadingTableContent = { isLoading };

  return (
    <ContentContainer loadingContent={loadingContent}>
      <>
        <Layout align="center" justify="space-between" spacing={8}>
          <Text as="h1" m={0} variant="title">
            {capitalize(dashboard.name)}
          </Text>
          <Layout align="center" spacing={4}>
            <EditCharts />
          </Layout>
        </Layout>

        <>
          <Layout align="flex-start" w="100%" spacing={2}>
            <Layout direction="column" flex={1}>
              <SearchV3BooleanSearchBar
                value={searchText}
                onSubmit={onSearch}
                isSubmitting={isLoading}
              />
            </Layout>
            <SaveDashboard
              dashboardData={dashboardData}
              query={queryToSave}
              isDashboardDirty={hasDataChanges}
              hasSavePermission={canSaveDashboard}
              onSaveSucess={onSaveSuccess}
            />
          </Layout>
          {isQueryFiltersEnabled ? (
            <QueryFilters
              enablePinnedFilters
              query={queryFilters ?? []}
              defaultFilters={selectedFilters.map((a) => a.fieldId)}
              onReset={() =>
                dispatch(
                  slices.dashboardV2.actions.resetFilters({
                    searchFilters: searchFilters ?? {},
                  }),
                )
              }
              onQueryChange={(updatedQuery) => {
                dispatch(
                  slices.dashboardV2.actions.setFiltersQuery(updatedQuery),
                );
                dispatch(slices.dashboardV2.actions.setPage(1));
                dispatch(slices.dashboardV2.actions.setDashboardDirty(true));
              }}
              crossFilters={crossFilters}
              onCrossFilterChange={onCrossFiltersChange}
            />
          ) : queryBuilder ? (
            <>
              <ReadOnlyComplexQuery
                searchFilters={searchFilters || {}}
                onClear={() => {
                  dispatch(
                    slices.dashboardV2.actions.setQueryBuilder(undefined),
                  );
                  dispatch(
                    slices.dashboardV2.actions.setQueryBuilderPanelVisible(
                      false,
                    ),
                  );
                }}
                onEdit={() => {
                  dispatch(
                    slices.dashboardV2.actions.setQueryBuilderPanelVisible(
                      true,
                    ),
                  );
                }}
                queryBuilder={queryBuilder}
              />
              <Layout align="center" spacing={2} role="group">
                <CrossFilters
                  searchFilters={fieldsWithLazyLoad}
                  filters={crossFilters}
                  onChange={onCrossFiltersChange}
                />
              </Layout>
            </>
          ) : (
            <Filters
              selectedFilters={selectedFilters}
              searchFilters={searchFiltersList}
              pinnedFiltersIds={localPinnedFilterIds}
              crossFilters={crossFilters}
              onChange={onFilterChange}
              onQueryButtonClick={openQueryBuilderPanel}
              onFiltersReset={onFiltersReset}
              onChangeCrossFilters={onCrossFiltersChange}
              onUpdatePinnedFilters={handleUpdatePinnedFilters}
            />
          )}
          <Box mt={2}>
            <DashboardV2Charts
              charts={chartFilters}
              queryParams={{
                booleanQuery: searchText,
                filters: activeFilters,
                crossFilters,
                searchFilters,
                queryBuilder,
              }}
              query={
                isQueryFiltersEnabled
                  ? isActiveQuery(queryFilters ?? [])
                  : undefined
              }
            />
          </Box>

          {isBulkActionOnDashboardEnabled && (
            <>
              {limitExceededModal}
              {panel}
            </>
          )}
          {!isErrorSearchDocuments && (
            <ContentContainer loadingContent={loadingTableContent}>
              <Box mt={2}>
                <DashboardV2SearchResults
                  currentTableView={currentTableView}
                  dashboard={dashboard}
                  searchFilters={searchFilters || {}}
                  isLoading={isFetchingSearchResults}
                  data={searchV3Data}
                  query={query}
                  tableSelection={tableSelection}
                  resultsCache={resultsCache}
                  pageSize={tableSettings?.pageSize}
                  refreshSearch={refreshSearchResult}
                  onMove={handleMove}
                  onCopy={handleCopy}
                  onEdit={handleEdit}
                  onEditClauses={handleEditClauses}
                  onDelete={handleDelete}
                  onAddToGroup={() => setIsAddToGroupVisible(true)}
                  onLoadView={showLoadView}
                  onSaveView={showSaveView}
                />
              </Box>
            </ContentContainer>
          )}
          {queryBuilderPanelVisible && (
            <QueryBuilder
              query={queryBuilder}
              searchFilters={searchFiltersList}
              onHide={hideQueryBuilderPanel}
              onQueryChange={handleSearchQueryBuilder}
              pinnedFiltersIds={localPinnedFilterIds}
            />
          )}

          {isAddToGroupVisible && (
            <AddSearchResultsToGroup
              query={query}
              tableSelection={tableSelection}
              onHide={() => setIsAddToGroupVisible(false)}
              onCompleted={handleSuccessAddToGroup}
            />
          )}

          {isSaveViewVisible && (
            <BaseSaveView
              context={TableViewContextType.SEARCH}
              fields={getSearchableFields(columns)}
              hideModal={() => setIsSaveViewVisible(false)}
              modalType={MODAL_DOCUMENTS_COLUMN_FIELDS_SAVE}
            />
          )}

          {isLoadViewVisible && (
            <BaseLoadView
              context={TableViewContextType.SEARCH}
              modalType={MODAL_DOCUMENTS_COLUMN_FIELDS_VIEW_SWITCH}
              hideModal={() => setIsLoadViewVisible(false)}
              onViewSelected={() => {
                fetchTableView({ context: TableViewContextType.SEARCH });
              }}
            />
          )}
        </>
      </>
    </ContentContainer>
  );
};

export default DashboardV2;
